import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { usePeriod } from '@/features/period/PeriodProvider';
import { Boton, Campo, Entrada, Error as Aviso, Importe, Seleccion } from '@/ui/Sistema';
import { Plegable } from '@/ui/Plegable';
import {
  formatCents,
  listPrices,
  MESES,
  parsePesosToCents,
  upsertPrice,
  type PriceKind,
  type ReferencePrice,
} from './catalog';

interface Props {
  organizationId: string;
  isAdmin: boolean;
  kind: PriceKind;
}

/** El nombre corto y la unidad, separados: el rótulo no es la explicación. */
const PRECIO: Record<PriceKind, { nombre: string; unidad: string }> = {
  steer: { nombre: 'Novillo', unidad: 'por kg' },
  fuel: { nombre: 'Combustible', unidad: 'por km' },
};

/**
 * Un precio de referencia, con su histórico detrás.
 *
 * **Lo que se viene a ver es el número de este mes.** Antes la tarjeta abría
 * con el formulario de carga desplegado y el histórico entero debajo, así que
 * para leer «$ 4.300» había que buscarlo entre dos selectores, un campo y una
 * lista de doce meses. Ahora el precio del período es lo primero y lo más
 * grande; cargar uno nuevo es un botón, y el histórico está plegado.
 *
 * Cargar el precio de un mes no altera ningún mes anterior. Y si un período no
 * tiene precio, el equivalente en pesos queda PENDIENTE: nunca se muestra $ 0.
 */
export function PricesCard({ organizationId, isAdmin, kind }: Props) {
  // El mismo período que el resto de la aplicación. Antes esta tarjeta miraba
  // siempre el mes de hoy y no había forma de consultar otro sin cambiar la
  // fecha del teléfono.
  const { year, month } = usePeriod();

  const [prices, setPrices] = useState<ReferencePrice[]>([]);
  const [editando, setEditando] = useState(false);
  const [anio, setAnio] = useState(year);
  const [mes, setMes] = useState(month);
  const [monto, setMonto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lista = await listPrices(organizationId, kind);
        if (!cancelado) setPrices(lista);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [organizationId, kind, recarga]);

  const guardar = async () => {
    setError(null);
    setOk(null);
    const cents = parsePesosToCents(monto);
    if (cents === null) {
      setError('Poné un importe mayor a cero');
      return;
    }
    try {
      await upsertPrice({ organizationId, kind, year: anio, month: mes, amountCents: cents });
      setMonto('');
      setOk(`Guardado para ${MESES[mes - 1]} ${anio}`);
      setEditando(false);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const delPeriodo = prices.find((p) => p.period_year === year && p.period_month === month);
  const { nombre, unidad } = PRECIO[kind];

  return (
    <section className="flex flex-col gap-3" data-testid={`prices-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold">{nombre}</h2>
          <p className="text-sm text-[var(--tinta-suave)]">
            {MESES[month - 1]} {year}
          </p>
          <p className="mt-1 text-2xl">
            <Importe
              pendiente={!delPeriodo}
              className="text-2xl"
              data-testid={`current-price-${kind}`}
            >
              {formatCents(delPeriodo?.amount_cents ?? null)}
            </Importe>
            <span className="ml-1 text-sm font-normal text-[var(--tinta-suave)]">{unidad}</span>
          </p>
        </div>

        {isAdmin && (
          <Boton
            onClick={() => {
              setAnio(year);
              setMes(month);
              setEditando((v) => !v);
            }}
            data-testid={`price-edit-${kind}`}
          >
            {editando ? 'Cancelar' : 'Editar'}
          </Boton>
        )}
      </div>

      <Aviso testId={`price-error-${kind}`}>{error}</Aviso>
      {ok && <p className="text-sm text-[var(--exito)]">{ok}</p>}

      {isAdmin && editando && (
        <div className="superficie-suave flex flex-col gap-3 p-3">
          <div className="flex gap-2">
            <Campo rotulo="Mes">
              <Seleccion
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                data-testid={`price-month-${kind}`}
              >
                {MESES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </Seleccion>
            </Campo>
            <Campo rotulo="Año">
              <Entrada
                type="number"
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                className="w-28"
                data-testid={`price-year-${kind}`}
              />
            </Campo>
          </div>
          <Campo rotulo="Importe" ayuda="Cambiar un mes no modifica los anteriores.">
            <Entrada
              inputMode="decimal"
              placeholder="4300"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              data-testid={`price-amount-${kind}`}
            />
          </Campo>
          <Boton
            jerarquia="primario"
            onClick={() => void guardar()}
            data-testid={`price-save-${kind}`}
          >
            Guardar precio
          </Boton>
        </div>
      )}

      {/* Abajo y plegado: se consulta de vez en cuando y no tiene por qué
          competir con el número del mes, que es a lo que se entra. */}
      <Plegable
        titulo="Meses anteriores"
        resumen={
          prices.length === 0
            ? 'Sin precios cargados'
            : `${prices.length} mes${prices.length === 1 ? '' : 'es'}`
        }
        testId={`price-history-${kind}`}
      >
        {prices.length === 0 ? (
          <p className="text-sm text-[var(--tinta-suave)]" data-testid={`no-prices-${kind}`}>
            Todavía no cargaste precios. Los kg de los trabajos se guardan igual; el equivalente en
            pesos queda pendiente.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--linea)] text-sm">
            {prices.map((p) => (
              <li key={p.id} className="flex justify-between py-2">
                <span className="text-[var(--tinta-suave)]">
                  {MESES[p.period_month - 1]} {p.period_year}
                </span>
                <Importe>{formatCents(p.amount_cents)}</Importe>
              </li>
            ))}
          </ul>
        )}
      </Plegable>
    </section>
  );
}
